import { DateTime } from 'luxon';
import { useI18nContext } from '@/i18n/i18n-react';
import type { OrdersDrilldownOrdersResponseDto } from '@/generated/korza-api-client/types.gen';
import { getLuxonLocale } from '@/lib/utils';
import { ORDER_SORTS, type OrderSort } from '@/lib/orders-drilldown';
import { formatCurrency } from '@/components/metrics/formatters';
import {
  CHANNEL_MIGRATION_COLOR,
  FIRST_PURCHASE_COLOR,
  IN_APP_REPURCHASE_COLOR,
} from '@/components/orders-composition-donut';
import { OrdersDrilldownCampaignCell } from '@/components/orders-drilldown/orders-drilldown-campaign-cell';
import {
  OrdersDrilldownSortHeader,
  getNextSort,
  getSortDirection,
} from '@/components/orders-drilldown/orders-drilldown-sort-header';
import { motion } from 'motion/react';
import {
  MotionTableRow,
  getRowEntrance,
} from '@/components/orders-drilldown/motion-table-row';
import { CustomerAvatar } from '@/components/orders-drilldown/customer-avatar';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

type OrderRow = OrdersDrilldownOrdersResponseDto['items'][number];

const KIND_COLORS: Record<OrderRow['kind'], string> = {
  in_app: IN_APP_REPURCHASE_COLOR,
  channel_migration: CHANNEL_MIGRATION_COLOR,
  first_purchase: FIRST_PURCHASE_COLOR,
};

const SKELETON_ROWS = 10;
const COLUMN_COUNT = 6;

function orDash(value: string | null | undefined) {
  return value?.trim() ? value : '—';
}

interface OrdersDrilldownOrdersTableProps {
  orders: OrderRow[];
  isLoading: boolean;
  /** Showing the previous page while the next one loads. */
  isRefreshing: boolean;
  /** Shows the order type column (repurchase kinds). */
  isRepurchase: boolean;
  /** Active server-side sort (date or amount). */
  sort: OrderSort;
  onSortChange: (sort: OrderSort) => void;
}

/**
 * Rendered inside the sheet's scroll area: the header sticks to the top of
 * that area, and pagination lives outside, in the sheet footer.
 */
export function OrdersDrilldownOrdersTable({
  orders,
  isLoading,
  isRefreshing,
  isRepurchase,
  sort,
  onSortChange,
}: OrdersDrilldownOrdersTableProps) {
  const { LL, locale } = useI18nContext();

  const kindLabels: Record<OrderRow['kind'], string> = {
    in_app: LL.metricsPage.ordersComposition.inAppRepurchase.label(),
    channel_migration:
      LL.metricsPage.ordersComposition.channelMigration.label(),
    first_purchase: LL.metricsPage.ordersComposition.firstPurchase.label(),
  };

  const maxAmount = Math.max(1, ...orders.map((order) => order.totalAmount));

  const sortBy = (field: 'date' | 'amount') => () => {
    const next = getNextSort(field, sort, ORDER_SORTS);
    if (next) onSortChange(next);
  };

  return (
    <Table containerClassName="overflow-visible">
      <TableHeader className="sticky top-0 z-10 bg-background shadow-[0_1px_0_hsl(var(--border))]">
        <TableRow>
          <OrdersDrilldownSortHeader
            label={LL.metricsPage.repurchaseDrilldown.columns.orderAndDate()}
            direction={getSortDirection('date', sort)}
            onSort={sortBy('date')}
          />
          <TableHead>
            {LL.metricsPage.repurchaseDrilldown.columns.customer()}
          </TableHead>
          <TableHead>
            {LL.metricsPage.repurchaseDrilldown.columns.products()}
          </TableHead>
          {isRepurchase ? (
            <TableHead>
              {LL.metricsPage.repurchaseDrilldown.columns.type()}
            </TableHead>
          ) : null}
          <TableHead>
            {LL.metricsPage.repurchaseDrilldown.columns.campaign()}
          </TableHead>
          <OrdersDrilldownSortHeader
            alignRight
            label={LL.metricsPage.repurchaseDrilldown.columns.amount()}
            direction={getSortDirection('amount', sort)}
            onSort={sortBy('amount')}
          />
        </TableRow>
      </TableHeader>
      <TableBody
        className={`transition-opacity duration-200 ${isRefreshing ? 'opacity-50' : ''}`}
      >
        {isLoading
          ? Array.from({ length: SKELETON_ROWS }).map((_, index) => (
              <TableRow key={`skeleton-${index}`}>
                {Array.from({
                  length: isRepurchase ? COLUMN_COUNT : COLUMN_COUNT - 1,
                }).map((__, cell) => (
                  <TableCell key={cell}>
                    <Skeleton className="h-4 w-full" />
                  </TableCell>
                ))}
              </TableRow>
            ))
          : orders.map((order, index) => (
              <MotionTableRow key={order.id} {...getRowEntrance(index)}>
                <TableCell className="py-2 whitespace-nowrap">
                  <div className="font-medium">{orDash(order.orderNumber)}</div>
                  <div className="text-xs text-muted-foreground">
                    {DateTime.fromISO(order.date)
                      .setLocale(getLuxonLocale(locale))
                      .toLocaleString(DateTime.DATETIME_SHORT)}
                  </div>
                </TableCell>
                <TableCell className="py-2 max-w-[260px]">
                  <div className="flex items-center gap-2.5">
                    <CustomerAvatar
                      name={order.customerName}
                      seed={order.customerEmail}
                    />
                    <div className="min-w-0">
                      <div className="truncate">
                        {orDash(order.customerName)}
                      </div>
                      <div className="truncate text-xs text-muted-foreground">
                        {orDash(order.customerEmail)}
                      </div>
                    </div>
                  </div>
                </TableCell>
                <TableCell
                  className="py-2 max-w-[240px] text-xs text-muted-foreground"
                  title={order.lineItemNames.join(', ')}
                >
                  <span className="line-clamp-2 break-words">
                    {order.lineItemNames.length > 0
                      ? order.lineItemNames.join(', ')
                      : '—'}
                  </span>
                </TableCell>
                {isRepurchase ? (
                  <TableCell className="py-2">
                    <Badge
                      variant="outline"
                      className="gap-1.5 whitespace-nowrap"
                    >
                      <span
                        className="inline-block size-2 rounded-full"
                        style={{ backgroundColor: KIND_COLORS[order.kind] }}
                        aria-hidden="true"
                      />
                      {kindLabels[order.kind]}
                    </Badge>
                  </TableCell>
                ) : null}
                <TableCell className="py-2">
                  <OrdersDrilldownCampaignCell campaign={order.campaign} />
                </TableCell>
                <TableCell className="relative py-2 whitespace-nowrap text-right font-medium">
                  <motion.span
                    aria-hidden="true"
                    className="absolute inset-y-1.5 right-0 rounded-l-md bg-gradient-to-l from-primary/15 to-transparent"
                    initial={{ width: 0 }}
                    animate={{
                      width: `${Math.max(8, (order.totalAmount / maxAmount) * 100)}%`,
                    }}
                    transition={{
                      delay: 0.15 + Math.min(index, 12) * 0.035,
                      duration: 0.6,
                      ease: [0.16, 1, 0.3, 1],
                    }}
                  />
                  <span className="relative">
                    {formatCurrency(order.totalAmount, locale, order.currency)}
                  </span>
                </TableCell>
              </MotionTableRow>
            ))}
      </TableBody>
    </Table>
  );
}
